// src/utils/quote/quoteApi.js
// fetch wrappers for the wizard's REST endpoints + the anonymous-events endpoint.
// All return parsed JSON on 2xx, throw Error on non-2xx (caller handles).

import { captureError } from "src/utils/errorReporting";
import { API_URL } from "src/utils/apiUrl";
import { jsonOrThrow, authHeaders } from "src/utils/apiFetch";
import { getAttribution } from "src/utils/attribution";

export async function checkAddress({ osoite, lat, lng, placeId, formattedAddress, requiredPuomi, token } = {}) {
    const body = { osoite };
    if (Number.isFinite(lat) && Number.isFinite(lng) && typeof placeId === "string" && placeId.length > 0) {
        body.lat = lat;
        body.lng = lng;
        body.placeId = placeId;
        if (typeof formattedAddress === "string" && formattedAddress.length > 0) {
            body.formattedAddress = formattedAddress;
        }
    }
    // Step-2 boom re-check: only a finite positive value filters; the backend
    // treats absent/invalid as "no boom filter".
    if (Number.isFinite(requiredPuomi) && requiredPuomi > 0) {
        body.requiredPuomi = requiredPuomi;
    }
    // Anonymous endpoint, but a logged-in developer's token unlocks the
    // covering-provider list in the response (backend gates on isDeveloper).
    const res = await fetch(`${API_URL}/api/pumppuRequests/checkAddress`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify(body),
    });
    return jsonOrThrow(res);
}

export async function createDraft({ token, draft, sessionId, language = "fi" }) {
    const res = await fetch(`${API_URL}/api/pumppuRequests/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify({ ...draft, sessionId, language, attribution: getAttribution() }),
    });
    return jsonOrThrow(res);
}

export async function patchDraft({ token, id, fields, signal }) {
    const res = await fetch(`${API_URL}/api/pumppuRequests/${id}/draft`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify(fields),
        signal,
    });
    return jsonOrThrow(res);
}

export async function sendDraft({ token, id, asiakasId, termsVersion, language = "fi" }) {
    const body = { language };
    if (asiakasId != null) body.asiakasId = asiakasId;
    if (termsVersion != null) body.termsVersion = termsVersion;
    const attribution = getAttribution();
    if (attribution) body.attribution = attribution;
    const res = await fetch(`${API_URL}/api/pumppuRequests/${id}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify(body),
    });
    return jsonOrThrow(res);
}

export async function getDraft({ token, id }) {
    const res = await fetch(`${API_URL}/api/pumppuRequests/${id}/draft`, {
        headers: authHeaders(token),
    });
    return jsonOrThrow(res);
}

export async function getActiveDraft({ token }) {
    const res = await fetch(`${API_URL}/api/pumppuRequests/draft/active`, {
        headers: authHeaders(token),
    });
    return jsonOrThrow(res);
}

export async function deleteDraft({ token, id }) {
    const res = await fetch(`${API_URL}/api/pumppuRequests/${id}/draft`, {
        method: "DELETE",
        headers: authHeaders(token),
    });
    return jsonOrThrow(res);
}

// Returns enriched attachment rows with fresh blob URLs. Used on wizard resume
// + ValuDetail to seed the AttachmentsSection thumbnail grid. Re-export of the
// helper that lives in quoteAttachments.js so the wizard's data layer keeps a
// single import surface in RequestForm.
export { listPumppuRequestAttachments } from "src/utils/quote/quoteAttachments";

// Both beacons below use bare fetch (no jsonOrThrow), so a non-2xx never throws
// — everything reaching their catch is a network-level failure, which for a
// fire-and-forget beacon means offline, blocked by an extension, or the tab
// closing mid-flight. None of those are actionable, and a real API outage shows
// up on every non-beacon endpoint anyway. Report only the unexpected rest.
const isNetworkFailure = (err) => err instanceof TypeError;

export async function postAnonymousEvent({ sessionId, step, partialDraft }) {
    try {
        await fetch(`${API_URL}/api/betonijerryAnonymousEvents`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ sessionId, step, partialDraft }),
        });
    } catch (err) {
        if (isNetworkFailure(err)) return;
        captureError(err, { tags: { entity: "betonijerryAnonymousEvent", swallowed: true } });
    }
}

// Anonymous presence heartbeat for the System Map's BetoniJerry hall. Best-effort
// and ungated (like postAnonymousEvent above); a dropped beat just ages out of
// the server-side Redis window. See src/hooks/usePresenceHeartbeat.js.
export async function postPresence({ anonId, stage, authed }) {
    try {
        await fetch(`${API_URL}/api/jerry/presence`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ anonId, stage, authed }),
            keepalive: true,
        });
    } catch (err) {
        if (isNetworkFailure(err)) return;
        captureError(err, { tags: { entity: "jerryPresence", swallowed: true } });
    }
}

/**
 * GET /api/me/asiakases — list asiakases the logged-in person can be billed as.
 * Returns the parsed array directly. 401 propagates as an Error with status set so
 * RequestForm's handleAuthError can log out silently.
 */
export async function listMeAsiakases({ token }) {
    const res = await fetch(`${API_URL}/api/me/asiakases`, {
        headers: authHeaders(token),
    });
    return jsonOrThrow(res);
}

/**
 * POST /api/me/asiakases — create a company asiakas under the BetoniJerry umbrella.
 * Spec: docs/superpowers/specs/2026-05-13-betonijerry-laskutus-step-design.md
 *
 * Returns the parsed body for 201 (new row) AND 409 (existing row already linked).
 * Throws for 400/401/500 — caller renders the body.message under the inline form.
 */
export async function createMeAsiakas({ token, ytunnus, asiakasNimi }) {
    const res = await fetch(`${API_URL}/api/me/asiakases`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify({ asiakasTypeId: 1, ytunnus, asiakasNimi }),
    });
    // 409 returns the existing row — surface it to the caller without throwing.
    if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        return { ...body, alreadyExisted: true };
    }
    return jsonOrThrow(res);
}

/**
 * POST /api/me/asiakases/private — find-or-create the caller's private
 * (asiakasTypeId=2) asiakas under the BetoniJerry umbrella.
 *
 * Used when the user picks "Laskuta yksityishenkilönä" and we need a real
 * asiakasId to PATCH onto the draft so the choice survives reload.
 *
 * Returns the row on 200 (existing) and 201 (newly created) — both shapes match.
 */
export async function createMePrivateAsiakas({ token, signal }) {
    const res = await fetch(`${API_URL}/api/me/asiakases/private`, {
        method: "POST",
        headers: authHeaders(token),
        signal,
    });
    return jsonOrThrow(res);
}

/**
 * PATCH /api/me/person — edit caller's own person fields + (optionally) the
 * billing address on the caller's private (typeId=2) asiakas. Backend writes
 * both stores in one transaction; if address fields are present and the
 * private asiakas doesn't exist yet, it's created on demand.
 *
 * Response: { person, privateAsiakas }. privateAsiakas is null when only
 * person fields were sent.
 * 409 (email taken): error has `.body.conflict === "email"`.
 */
export async function patchMePerson({ token, fields, signal }) {
    const res = await fetch(`${API_URL}/api/me/person`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify(fields),
        signal,
    });
    return jsonOrThrow(res);
}

/**
 * PATCH /api/me/asiakases/:id — edit a company asiakas the caller owns under
 * the BetoniJerry umbrella. Private (typeId=2) rows are rejected by the
 * backend; route them through patchMePerson instead.
 *
 * 409 (y-tunnus taken): error has `.body.conflict === "ytunnus"`.
 */
export async function patchMeAsiakas({ token, id, fields, signal }) {
    const res = await fetch(`${API_URL}/api/me/asiakases/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify(fields),
        signal,
    });
    return jsonOrThrow(res);
}
